/**
 * ArchiveListDialog — confirmation dialog shown before archiving a list.
 * Offers the user a choice: archive cards with the list, or move them
 * to another list first.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ListOption {
  readonly listId: string;
  readonly title: string;
}

export interface ArchiveListDialogProps {
  readonly open: boolean;
  readonly listTitle: string;
  readonly cardCount: number;
  readonly otherLists: readonly ListOption[];
  readonly onArchiveWithCards: () => void;
  readonly onMoveCardsAndArchive: (targetListId: string) => void;
  readonly onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ArchiveListDialog({
  open,
  listTitle,
  cardCount,
  otherLists,
  onArchiveWithCards,
  onMoveCardsAndArchive,
  onCancel,
}: ArchiveListDialogProps): React.ReactElement {
  const [selectedListId, setSelectedListId] = useState<string>('');
  const [action, setAction] = useState<'archive' | 'move'>('archive');

  const hasCards = cardCount > 0;
  const hasOtherLists = otherLists.length > 0;

  const handleConfirm = () => {
    if (action === 'move' && selectedListId) {
      onMoveCardsAndArchive(selectedListId);
    } else {
      onArchiveWithCards();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onCancel(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Archive list "{listTitle}"</DialogTitle>
          <DialogDescription>
            {hasCards
              ? `This list has ${cardCount} card${cardCount === 1 ? '' : 's'}. What would you like to do with them?`
              : 'This list has no cards. It will be archived and hidden from the board.'}
          </DialogDescription>
        </DialogHeader>

        {hasCards && (
          <div className="space-y-3 py-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="archive-action"
                value="archive"
                checked={action === 'archive'}
                onChange={() => setAction('archive')}
                className="mt-0.5"
              />
              <div>
                <p className="text-sm font-medium text-card-foreground">Archive cards with the list</p>
                <p className="text-xs text-muted-foreground">All cards will be archived and can be restored later.</p>
              </div>
            </label>

            {hasOtherLists && (
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="archive-action"
                  value="move"
                  checked={action === 'move'}
                  onChange={() => setAction('move')}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-card-foreground">Move cards to another list</p>
                  <p className="text-xs text-muted-foreground mb-2">Cards will be moved before the list is archived.</p>
                  {action === 'move' && (
                    <Select value={selectedListId} onValueChange={setSelectedListId}>
                      <SelectTrigger className="w-full" aria-label="Target list">
                        <SelectValue placeholder="Select a list…" />
                      </SelectTrigger>
                      <SelectContent>
                        {otherLists.map((list) => (
                          <SelectItem key={list.listId} value={list.listId}>
                            {list.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </label>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={action === 'move' && !selectedListId}
          >
            {hasCards && action === 'move' ? 'Move & Archive' : 'Archive List'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
